import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Box,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Card,
  CardContent,
  Alert,
  Tabs,
  Tab,
  Chip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Settings as SettingsIcon } from '@mui/icons-material';
import moment from 'moment';
import financeStore from '../stores/FinanceStore';
import EmployeeCommissionModal from '../components/EmployeeCommissionModal';
import CommissionSettingsModal from '../components/CommissionSettingsModal';

const FinanceDashboard = observer(() => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);

  useEffect(() => {
    financeStore.loadSummary();
    financeStore.loadTransactions();
  }, []);

  const handleYearChange = (event) => {
    financeStore.setPeriod(event.target.value, financeStore.selectedMonth);
    financeStore.loadSummary();
    financeStore.loadTransactions();
  };

  const handleMonthChange = (event) => {
    financeStore.setPeriod(financeStore.selectedYear, event.target.value);
    financeStore.loadSummary();
    financeStore.loadTransactions();
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleRowClick = (params) => {
    setSelectedEmployee({
      id: params.row.employee.id,
      name: params.row.employee.name
    });
    setEmployeeModalOpen(true);
  };

  const handleCloseEmployeeModal = () => {
    setEmployeeModalOpen(false);
    setSelectedEmployee(null);
  };

  const handleOpenSettings = () => {
    setSettingsModalOpen(true);
  };

  const handleCloseSettings = () => {
    setSettingsModalOpen(false);
  };

  const formatCurrency = (value) => {
    const numValue = Number(value);
    if (isNaN(numValue)) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(numValue);
  };

  // Generate year options (current year and 2 years back)
  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  // Month options (1-12)
  const monthOptions = Array.from({ length: 12 }, (_, i) => i + 1);

  // ===== COMMISSIONS TAB DATA =====
  const summary = financeStore.summary || [];
  const totals = summary.reduce(
    (acc, item) => ({
      totalServicePrice: acc.totalServicePrice + (item.totalServicePrice || 0),
      totalCommissionAmount: acc.totalCommissionAmount + (item.totalCommissionAmount || 0),
      totalPaidAmount: acc.totalPaidAmount + (item.totalPaidAmount || 0),
      paidCommissionAmount: acc.paidCommissionAmount + (item.paidCommissionAmount || 0),
      pendingCommissionAmount: acc.pendingCommissionAmount + (item.pendingCommissionAmount || 0),
      bookingCount: acc.bookingCount + (item.bookingCount || 0)
    }),
    { totalServicePrice: 0, totalCommissionAmount: 0, totalPaidAmount: 0, paidCommissionAmount: 0, pendingCommissionAmount: 0, bookingCount: 0 }
  );

  // Pending payout = commission still owed to employees (not yet marked as paid)
  const pendingPayout = totals.pendingCommissionAmount;

  const commissionsColumns = [
    {
      field: 'employee',
      headerName: 'Funcionário',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params.name
    },
    {
      field: 'bookingCount',
      headerName: 'Agendamentos',
      width: 130,
      type: 'number'
    },
    {
      field: 'totalServicePrice',
      headerName: 'Total Serviços',
      width: 140,
      valueFormatter: (params) => formatCurrency(params)
    },
    {
      field: 'totalCommissionAmount',
      headerName: 'Total Comissão',
      width: 140,
      valueFormatter: (params) => formatCurrency(params)
    },
    {
      field: 'pendingCommissionAmount',
      headerName: 'Pendente Pagamento',
      width: 170,
      valueFormatter: (params) => formatCurrency(params)
    }
  ];

  // ===== TRANSACTIONS TAB DATA =====
  const transactions = financeStore.transactions || [];

  const transactionsColumns = [
    {
      field: 'date',
      headerName: 'Data',
      width: 120,
      valueFormatter: (params) => moment(params).format('DD/MM/YYYY')
    },
    {
      field: 'type',
      headerName: 'Tipo',
      width: 150,
      renderCell: (params) => {
        const isIncoming = params.value === 'payment';
        return (
          <Chip
            label={isIncoming ? 'Recebimento' : 'Comissão'}
            size="small"
            color={isIncoming ? 'success' : 'warning'}
          />
        );
      }
    },
    {
      field: 'description',
      headerName: 'Descrição',
      flex: 1,
      minWidth: 200
    },
    {
      field: 'employee',
      headerName: 'Funcionário',
      width: 150,
      valueGetter: (params) => params?.name || '-'
    },
    {
      field: 'customer',
      headerName: 'Cliente',
      width: 150,
      valueGetter: (params) => params?.name || '-'
    },
    {
      field: 'amount',
      headerName: 'Valor',
      width: 130,
      renderCell: (params) => {
        const amount = Number(params.value);
        if (isNaN(amount)) return formatCurrency(0);

        const isIncoming = params.row.type === 'payment';
        return (
          <span style={{
            color: isIncoming ? '#2e7d32' : '#d32f2f',
            fontWeight: 500
          }}>
            {isIncoming ? '+' : '-'} {formatCurrency(Math.abs(amount))}
          </span>
        );
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => {
        const statusMap = {
          confirmed: { label: 'Confirmado', color: 'success' },
          paid: { label: 'Pago', color: 'success' },
          pending: { label: 'Pendente', color: 'warning' },
          cancelled: { label: 'Cancelado', color: 'error' }
        };
        const status = statusMap[params.value] || { label: params.value || 'N/A', color: 'default' };
        return (
          <Chip
            label={status.label}
            size="small"
            color={status.color}
          />
        );
      }
    }
  ];

  // Calculate transaction totals
  const transactionTotals = transactions.reduce(
    (acc, tx) => {
      const amount = Number(tx.amount);
      if (!isNaN(amount)) {
        if (tx.type === 'payment') {
          acc.incoming += amount;
        } else if (tx.type === 'commission') {
          acc.outgoing += amount;
        }
      }
      return acc;
    },
    { incoming: 0, outgoing: 0 }
  );

  const balance = transactionTotals.incoming - transactionTotals.outgoing;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Financeiro
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Month Selector */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Mês</InputLabel>
            <Select
              value={financeStore.selectedMonth}
              onChange={handleMonthChange}
              label="Mês"
            >
              {monthOptions.map(month => (
                <MenuItem key={month} value={month}>
                  {moment().month(month - 1).format('MMMM')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Year Selector */}
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Ano</InputLabel>
            <Select
              value={financeStore.selectedYear}
              onChange={handleYearChange}
              label="Ano"
            >
              {yearOptions.map(year => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Settings Button */}
          <IconButton onClick={handleOpenSettings} color="primary">
            <SettingsIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Error Display */}
      {financeStore.error && (
        <Alert severity="error" onClose={() => financeStore.clearMessages()} sx={{ mb: 2 }}>
          {financeStore.error}
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Comissões" />
          <Tab label="Transações" />
        </Tabs>
      </Box>

      {/* COMMISSIONS TAB */}
      {tabValue === 0 && (
        <>
          {/* Summary Cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Total Serviços
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(totals.totalServicePrice)}
                </Typography>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Total Comissão
                </Typography>
                <Typography variant="h5" color="primary">
                  {formatCurrency(totals.totalCommissionAmount)}
                </Typography>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Pendente Pagamento
                </Typography>
                <Typography variant="h5" sx={{ color: '#ed6c02' }}>
                  {formatCurrency(pendingPayout)}
                </Typography>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Agendamentos
                </Typography>
                <Typography variant="h5">
                  {totals.bookingCount}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Employee Commission Table */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Comissões por Funcionário
            </Typography>
            <Paper sx={{ height: 500, width: '100%' }}>
              <DataGrid
                rows={summary}
                columns={commissionsColumns}
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50]}
                loading={financeStore.isLoading}
                disableSelectionOnClick
                getRowId={(row) => row.employee.id}
                onRowClick={handleRowClick}
                sx={{
                  '& .MuiDataGrid-row': {
                    cursor: 'pointer'
                  }
                }}
              />
            </Paper>
          </Box>
        </>
      )}

      {/* TRANSACTIONS TAB */}
      {tabValue === 1 && (
        <>
          {/* Summary Cards */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Paper sx={{ p: 2, flex: 1 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Recebimentos
              </Typography>
              <Typography variant="h5" sx={{ color: '#2e7d32' }}>
                {formatCurrency(transactionTotals.incoming)}
              </Typography>
            </Paper>

            <Paper sx={{ p: 2, flex: 1 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Comissões Pagas
              </Typography>
              <Typography variant="h5" sx={{ color: '#d32f2f' }}>
                {formatCurrency(transactionTotals.outgoing)}
              </Typography>
            </Paper>

            <Paper sx={{ p: 2, flex: 1 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Saldo do Período
              </Typography>
              <Typography variant="h5" sx={{ color: balance >= 0 ? '#2e7d32' : '#d32f2f' }}>
                {formatCurrency(balance)}
              </Typography>
            </Paper>
          </Box>

          {/* Transactions Table */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Todas as Transações
            </Typography>
            <Paper sx={{ height: 600, width: '100%' }}>
              <DataGrid
                rows={transactions}
                columns={transactionsColumns}
                pageSize={25}
                rowsPerPageOptions={[25, 50, 100]}
                loading={financeStore.isLoading}
                disableSelectionOnClick
                getRowId={(row) => row.id}
                initialState={{
                  sorting: {
                    sortModel: [{ field: 'date', sort: 'desc' }]
                  }
                }}
              />
            </Paper>
          </Box>
        </>
      )}

      {/* Employee Commission Detail Modal */}
      {selectedEmployee && (
        <EmployeeCommissionModal
          open={employeeModalOpen}
          onClose={handleCloseEmployeeModal}
          employeeId={selectedEmployee.id}
          employeeName={selectedEmployee.name}
        />
      )}

      {/* Commission Settings Modal */}
      <CommissionSettingsModal
        open={settingsModalOpen}
        onClose={handleCloseSettings}
      />
    </Box>
  );
});

export default FinanceDashboard;
